import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import SEOHead from '@/components/SEOHead';
import { Lock, Eye, EyeOff, Check, AlertCircle, ArrowRight } from 'lucide-react';

// ─── /reset-password ──────────────────────────────────────────────────────────
// The page the emailed link lands on.
//
// The server has always sent `${CLIENT_URL}/reset-password?token=…` and has both
// endpoints (routes/auth.js), but this page did not exist — so every reset email
// ever sent landed on the 404. The request half worked, which is why it went
// unnoticed: people got the email, clicked it, and hit a dead end.
//
// Not behind auth, by definition: whoever is here cannot sign in. The token in
// the query string is the credential, and the server hashes it and checks expiry
// before it will change anything.

// The server rejects anything shorter, so enforce the same number here rather
// than letting someone type a password and be told no by the API.
const MIN_LENGTH = 6;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [status, setStatus] = useState('idle');   // idle | saving | done
  const [error, setError] = useState('');

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= MIN_LENGTH && confirm === password && status !== 'saving';

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('saving');
    setError('');

    const { error: err } = await supabase.auth.confirmPasswordReset({ token, password });
    if (err) {
      setStatus('idle');
      setError(err.message);
      return;
    }
    setStatus('done');
  };

  // No token at all — someone typed the path, or a mail client mangled the link.
  if (!token) {
    return (
      <Shell title="That link is incomplete">
        <p className="text-zinc-400 text-sm leading-relaxed">
          This page needs the link from your reset email. Open that email and click
          through again — some mail apps cut long links in half.
        </p>
        <Button
          onClick={() => navigate('/')}
          className="mt-6 h-10 px-5 bg-orange-500 hover:bg-orange-400 text-ink rounded-xl text-sm font-semibold"
        >
          Go home
        </Button>
      </Shell>
    );
  }

  if (status === 'done') {
    return (
      <Shell title="Password changed">
        <div className="flex items-center gap-2 text-emerald-400 text-sm mb-3">
          <Check className="w-4 h-4" /> You can sign in with it now.
        </div>
        <p className="text-zinc-400 text-sm leading-relaxed">
          The reset link has been used up, so it will not work again.
        </p>
        <Button
          onClick={() => navigate('/')}
          className="mt-6 h-10 px-5 bg-orange-500 hover:bg-orange-400 text-ink rounded-xl text-sm font-semibold gap-2"
        >
          Sign in <ArrowRight className="w-4 h-4" />
        </Button>
      </Shell>
    );
  }

  return (
    <Shell title="Choose a new password">
      <SEOHead title="Reset your password" noIndex />
      <p className="text-zinc-400 text-sm leading-relaxed mb-6">
        Pick something you have not used here before. At least {MIN_LENGTH} characters.
      </p>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field
          label="New password"
          value={password}
          onChange={setPassword}
          show={show}
          onToggle={() => setShow(v => !v)}
          autoComplete="new-password"
          error={tooShort ? `At least ${MIN_LENGTH} characters` : ''}
        />
        <Field
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          show={show}
          autoComplete="new-password"
          error={mismatch ? 'These do not match' : ''}
        />

        {error && (
          <div className="flex items-start gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span className="leading-relaxed">
              {error}
              {/* An expired token is the common case, and the only way out is a
                  fresh email — so say that rather than leaving them retrying. */}
              {/expired|invalid/i.test(error) && ' Request a new link from the sign-in screen.'}
            </span>
          </div>
        )}

        <Button
          type="submit"
          disabled={!canSubmit}
          className="mt-2 h-11 bg-orange-500 hover:bg-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-ink rounded-xl text-sm font-semibold"
        >
          {status === 'saving' ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ title, children }) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-zinc-900/70 border border-zinc-800 rounded-2xl p-7"
      >
        <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-orange-400" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2 tracking-tight">{title}</h1>
        {children}
      </motion.div>
    </div>
  );
}

function Field({ label, value, onChange, show, onToggle, error, autoComplete }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-500 mb-1.5 block">{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className={`w-full bg-zinc-950 border rounded-xl px-3.5 py-2.5 pr-10 text-sm text-white placeholder-zinc-600 focus:outline-none transition-colors ${
            error ? 'border-red-500/50 focus:border-red-500' : 'border-zinc-800 focus:border-orange-500'
          }`}
        />
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={show ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-300"
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
      {error && <span className="text-[11px] text-red-400 mt-1 block">{error}</span>}
    </label>
  );
}
